/**
 * Phonetic Analysis Utilities
 * Consonant families, voicing rules, and phoneme extraction
 */

/**
 * Consonant families grouped by articulation manner
 * Used for family-based rhyme detection
 */
export const CONSONANT_FAMILIES = {
  VOICED_PLOSIVES: ['B', 'D', 'G'],
  VOICED_FRICATIVES: ['V', 'DH', 'Z', 'ZH', 'JH'],
  NASALS: ['M', 'N', 'NG'],
  UNVOICED_PLOSIVES: ['P', 'T', 'K'],
  UNVOICED_FRICATIVES: ['F', 'TH', 'S', 'SH', 'CH'],
  LIQUIDS: ['L', 'R'],
  GLIDES: ['W', 'Y', 'HH']
} as const;

/**
 * Voicing partner pairs (voiced ↔ unvoiced)
 * Maps consonants to their voicing counterparts
 */
export const VOICING_PARTNERS: Record<string, string> = {
  // Voiced → Unvoiced
  'B': 'P',
  'D': 'T',
  'G': 'K',
  'V': 'F',
  'DH': 'TH',
  'Z': 'S',
  'ZH': 'SH',
  'JH': 'CH',
  // Unvoiced → Voiced
  'P': 'B',
  'T': 'D',
  'K': 'G',
  'F': 'V',
  'TH': 'DH',
  'S': 'Z',
  'SH': 'ZH',
  'CH': 'JH'
};

/**
 * All vowel phonemes (with stress markers removed for checking)
 */
const VOWELS = new Set([
  'AA', 'AE', 'AH', 'AO', 'AW', 'AY', 'EH', 'ER', 'EY', 'IH', 'IY', 'OW', 'OY', 'UH', 'UW'
]);

/**
 * Strip stress markers from phoneme (0, 1, 2)
 */
export function stripStress(phoneme: string): string {
  return phoneme.replace(/[012]$/, '');
}

/**
 * Check if phoneme is a vowel (ignoring stress markers)
 */
export function isVowel(phoneme: string): boolean {
  return VOWELS.has(stripStress(phoneme));
}

/**
 * Check if phoneme is a consonant (not a vowel)
 */
export function isConsonant(phoneme: string): boolean {
  return !isVowel(phoneme);
}

/**
 * Check if consonant is voiced
 */
export function isVoicedConsonant(phoneme: string): boolean {
  const clean = stripStress(phoneme);
  return (
    (CONSONANT_FAMILIES.VOICED_PLOSIVES as readonly string[]).includes(clean) ||
    (CONSONANT_FAMILIES.VOICED_FRICATIVES as readonly string[]).includes(clean) ||
    (CONSONANT_FAMILIES.NASALS as readonly string[]).includes(clean)
  );
}

/**
 * Check if consonant is unvoiced
 */
export function isUnvoicedConsonant(phoneme: string): boolean {
  const clean = stripStress(phoneme);
  return (
    (CONSONANT_FAMILIES.UNVOICED_PLOSIVES as readonly string[]).includes(clean) ||
    (CONSONANT_FAMILIES.UNVOICED_FRICATIVES as readonly string[]).includes(clean)
  );
}

/**
 * Get the voicing partner of a consonant (B↔P, D↔T, etc.)
 * Returns null if no partner exists
 */
export function getVoicingPartner(phoneme: string): string | null {
  const clean = stripStress(phoneme);
  return VOICING_PARTNERS[clean] || null;
}

/**
 * Check if two consonants are in the same family
 */
export function areInSameFamily(c1: string, c2: string): boolean {
  const clean1 = stripStress(c1);
  const clean2 = stripStress(c2);

  // Check each family
  for (const family of Object.values(CONSONANT_FAMILIES)) {
    const familyArr = family as readonly string[];
    if (familyArr.includes(clean1) && familyArr.includes(clean2)) {
      return true;
    }
  }

  return false;
}

/**
 * Extract rhyming portion (from last stressed vowel to end)
 * CMU stress markers: 0 (no stress), 1 (primary), 2 (secondary)
 *
 * @deprecated Use extractRhymeAnchor for rhyme classification. This function
 * is kept for backward compatibility but is no longer used by the detection engine.
 */
export function extractRhymingPortion(phonemes: string[]): string[] {
  if (!phonemes || phonemes.length === 0) return [];

  // Find the last stressed vowel (1 or 2)
  let lastStressedIndex = -1;
  for (let i = phonemes.length - 1; i >= 0; i--) {
    if (phonemes[i].match(/[12]$/)) {
      lastStressedIndex = i;
      break;
    }
  }

  // If no stressed vowel found, use entire phoneme sequence
  if (lastStressedIndex === -1) return phonemes;

  // Return from last stressed vowel to end
  return phonemes.slice(lastStressedIndex);
}

/**
 * Get phonemes for a word.
 * Returns full phoneme array (not just rhyming portion).
 *
 * Local CMU dict removed — backend is now the source of truth.
 * The 11K-word local subset was always a subset of the full 134K-word CMU dict
 * on the backend, so nothing is lost and coverage only improves.
 *
 * @param word - The word to look up
 * @param useBackend - Enable backend lookup (default: true)
 */
export async function getPhonemes(word: string, useBackend: boolean = true): Promise<string[] | null> {
  if (useBackend) {
    return fetchPhonemesFromBackend(word);
  }
  return null;
}

/**
 * Fetch phonemes from backend /api/prosody/phonemes/{word} endpoint.
 * Hits the full 134K-word CMU dict — much broader than the local subset.
 *
 * @param word - The word to look up
 * @returns Full phoneme sequence or null on failure / not found
 */
async function fetchPhonemesFromBackend(word: string): Promise<string[] | null> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return null;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const encoded = encodeURIComponent(word.toLowerCase().trim());
    const response = await fetch(`/api/prosody/phonemes/${encoded}`, {
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) return null;

    const data = await response.json();
    return Array.isArray(data.phonemes) && data.phonemes.length > 0
      ? data.phonemes
      : null;
  } catch {
    return null;
  }
}

/**
 * Get rhyming portion of phonemes for a word
 * Combines phoneme lookup with rhyming portion extraction
 *
 * @param word - The word to look up
 * @param useBackend - Enable backend fallback for unknown words (default: true)
 */
export async function getRhymingPhonemes(word: string, useBackend: boolean = true): Promise<string[] | null> {
  const phonemes = await getPhonemes(word, useBackend);
  if (!phonemes) return null;
  return extractRhymingPortion(phonemes);
}

/**
 * Extract the final syllable's nucleus and coda from a phoneme sequence.
 *
 * Unlike extractRhymingPortion (which goes back to the last STRESSED vowel),
 * this goes back to the last vowel of ANY stress level. The result is the
 * minimal rhyming unit — the sound of the final syllable.
 *
 * This matters for multi-syllable words with an unstressed last syllable:
 *   "hobo" = HH OW1 B OW0 → last stressed vowel at idx 1 → "OW_B_OW"
 *                          → last vowel at idx 3         → "OW"  ← what we want
 *
 * Single-syllable words are unaffected (their only vowel is the last vowel).
 */
function extractFinalSyllableRhyming(phonemes: string[]): string[] {
  if (!phonemes || phonemes.length === 0) return [];

  let lastVowelIdx = -1;
  for (let i = phonemes.length - 1; i >= 0; i--) {
    if (isVowel(phonemes[i])) {
      lastVowelIdx = i;
      break;
    }
  }

  if (lastVowelIdx === -1) return phonemes;
  return phonemes.slice(lastVowelIdx);
}

/**
 * CANONICAL RHYME ANCHOR — the single source of truth for "what portion
 * of a word's phonemes determines its rhyme?"
 *
 * Uses the FINAL SYLLABLE (last vowel to end) as the anchor. Both
 * getPhoneticSignature (grouping) and detectRhymeCategory (classification)
 * must use this function to stay consistent.
 *
 * Contract guarantee:
 *   If extractRhymeAnchor(A) === extractRhymeAnchor(B) (stress-stripped),
 *   then detectRhymeCategory(A, B) !== NONE
 *
 * See also: getPhoneticSignature() which uses this same extraction
 * for its grouping key.
 */
export function extractRhymeAnchor(phonemes: string[]): string[] {
  return extractFinalSyllableRhyming(phonemes);
}

/**
 * Get a normalized phonetic signature for rhyme grouping.
 *
 * Uses the FINAL SYLLABLE's nucleus+coda (last vowel to end), not the
 * traditional "last stressed vowel to end", so that multi-syllable words
 * ending with an unstressed syllable group with single-syllable rhymes:
 *
 *   "hobo"  (HH OW1 B OW0) → "OW"  ← groups with foe and toe
 *   "foe"   (F OW1)         → "OW"
 *   "toe"   (T OW1)         → "OW"
 *   "head"  (HH EH1 D)      → "EH_D"  ← single-syllable behavior unchanged
 *   "said"  (S EH1 D)       → "EH_D"
 *
 * Note: detectRhymeCategory now uses extractRhymeAnchor (same final-syllable
 * logic) for consistent grouping+detection.
 *
 * @param word - The word to get signature for
 * @param useBackend - Enable backend fallback for unknown words (default: true)
 * @returns Phonetic signature string, or null if word not found
 */
export async function getPhoneticSignature(word: string, useBackend: boolean = true): Promise<string | null> {
  const phonemes = await getPhonemes(word, useBackend);
  if (!phonemes || phonemes.length === 0) return null;

  const finalSyllable = extractFinalSyllableRhyming(phonemes);
  if (finalSyllable.length === 0) return null;

  // Strip stress markers and join with underscores for a canonical key
  return finalSyllable.map(stripStress).join('_');
}
